import { useSearchParams } from 'react-router-dom';
import {
  APPLICATION_ID_STORAGE_KEY,
  CHECKLIST_VISIT_SNAPSHOT_KEY,
  ONBOARDING_CHECKLIST_STEPS,
  RESUME_TOKEN_STORAGE_KEY,
} from './catalog.js';

export function useApplicationIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('applicationId') ?? params.get('id') ?? getStoredApplicationId();
}

export function getStoredApplicationId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(APPLICATION_ID_STORAGE_KEY);
}

export function setStoredApplicationId(applicationId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(APPLICATION_ID_STORAGE_KEY, applicationId);
}

export function getStoredResumeToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(RESUME_TOKEN_STORAGE_KEY);
}

export function setStoredResumeToken(resumeToken: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(RESUME_TOKEN_STORAGE_KEY, resumeToken);
}

/** After submit the tenant path takes over; a lingering id would keep the draft view alive. */
export function clearStoredApplication(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(APPLICATION_ID_STORAGE_KEY);
  localStorage.removeItem(RESUME_TOKEN_STORAGE_KEY);
}

export function onboardingChecklistPath(applicationId?: string): string {
  return applicationId
    ? `/onboarding-checklist?applicationId=${encodeURIComponent(applicationId)}`
    : '/onboarding-checklist';
}

export function businessDetailsPath(applicationId?: string): string {
  return applicationId
    ? `/business-details?applicationId=${encodeURIComponent(applicationId)}`
    : '/business-details';
}

export function beatPaySetupPath(): string {
  return '/beat-pay-setup';
}

export function commercialTermsPath(): string {
  return '/commercial-terms';
}

export type ChecklistVisitSnapshot = {
  completedCodes: string[];
  seenAt: string;
};

export function readChecklistVisitSnapshot(): ChecklistVisitSnapshot | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(CHECKLIST_VISIT_SNAPSHOT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ChecklistVisitSnapshot;
    if (!Array.isArray(parsed.completedCodes)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeChecklistVisitSnapshot(completedCodes: string[]): void {
  if (typeof localStorage === 'undefined') return;
  const snapshot: ChecklistVisitSnapshot = {
    completedCodes,
    seenAt: new Date().toISOString(),
  };
  localStorage.setItem(CHECKLIST_VISIT_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

/** Labels for steps completed since the merchant last opened the checklist (07-05 §16). */
export function newlyCompletedStepLabels(
  currentCompletedCodes: string[],
  previous = readChecklistVisitSnapshot(),
): string[] {
  if (!previous) return [];
  const seen = new Set(previous.completedCodes);
  return currentCompletedCodes
    .filter((code) => !seen.has(code))
    .map((code) => ONBOARDING_CHECKLIST_STEPS.find((step) => step.code === code)?.label ?? code);
}
